import { useEffect, useRef, useCallback } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

const createChargerIconWithPrice = (available, selected, price) => {
  const color = available ? '#3d9e5f' : '#9ca3af'
  const size = selected ? 44 : 36
  const shadow = available ? `0 0 12px ${color}88` : 'none'

  return L.divIcon({
    className: '',
    html: `
      <div style="position: relative; width: ${size}px; height: ${size}px;">
        <div style="
          width: ${size}px; height: ${size}px; border-radius: 50%;
          background: ${available ? color : '#f3f4f6'};
          border: 3px solid ${selected ? 'white' : color};
          display: flex; align-items: center; justify-content: center;
          box-shadow: ${shadow}; cursor: pointer;">
          <svg width="${Math.round(size * 0.5)}" height="${Math.round(size * 0.5)}" viewBox="0 0 24 24" fill="none">
            <rect x="4" y="2" width="12" height="17" rx="2"
              stroke="${available ? 'white' : '#9ca3af'}" stroke-width="1.8" fill="none"/>
            <path d="M8 10L11 7V11H13L10 15V11H8Z" fill="${available ? 'white' : '#9ca3af'}"/>
          </svg>
        </div>
        <div style="
          position: absolute; top: -24px; left: 50%; transform: translateX(-50%);
          background: white; border: 1px solid #e5e7eb; border-radius: 8px;
          padding: 2px 7px; font-size: 10px; font-weight: 800; color: ${color};
          white-space: nowrap; font-family: DM Sans, sans-serif;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);">€${price}</div>
      </div>`,
    iconSize: [size, size + 24],
    iconAnchor: [size / 2, size / 2 + 12],
  })
}

const MapView = ({ chargers, selected, onSelect, locationTarget }) => {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef({})
  const locationMarkerRef = useRef(null)
  const onSelectRef = useRef(onSelect)

  // Keep onSelectRef current without triggering re-renders
  useEffect(() => {
    onSelectRef.current = onSelect
  }, [onSelect])

  useEffect(() => {
    if (mapInstanceRef.current) return

    mapInstanceRef.current = L.map(mapRef.current, {
      center: [52.3676, 4.9041],
      zoom: 12,
      zoomControl: true,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(mapInstanceRef.current)

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.setView(
              [pos.coords.latitude, pos.coords.longitude], 14
            )
          }
        },
        () => {}
      )
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [])

  // Stable marker click handler using ref
  const handleMarkerClick = useCallback((charger) => {
    onSelectRef.current(charger)
  }, [])

  useEffect(() => {
    if (!mapInstanceRef.current) return

    Object.values(markersRef.current).forEach(marker => marker.remove())
    markersRef.current = {}

    chargers.forEach(charger => {
      if (!charger.latitude || !charger.longitude) return

      const isSelected = selected?.id === charger.id
      const icon = createChargerIconWithPrice(
        charger.is_available, isSelected, charger.price_per_kwh
      )

      const marker = L.marker([charger.latitude, charger.longitude], { icon })
        .addTo(mapInstanceRef.current)

      marker.on('click', () => handleMarkerClick(charger))
      markersRef.current[charger.id] = marker
    })
  }, [chargers, selected, handleMarkerClick])

  useEffect(() => {
    if (!mapInstanceRef.current || !selected) return
    if (!selected.latitude || !selected.longitude) return
    try {
      mapInstanceRef.current.panTo(
        [selected.latitude, selected.longitude],
        { animate: true, duration: 0.5 }
      )
    } catch (e) {}
  }, [selected])

  useEffect(() => {
    if (!mapInstanceRef.current || !locationTarget) return
    try {
      if (locationMarkerRef.current) {
        locationMarkerRef.current.remove()
        locationMarkerRef.current = null
      }

      mapInstanceRef.current.setView(
        [locationTarget.lat, locationTarget.lon], 15, { animate: true }
      )

      const locationIcon = L.divIcon({
        className: '',
        html: `<div style="width:20px;height:20px;border-radius:50%;background:#3b82f6;border:3px solid white;box-shadow:0 0 12px #3b82f688;"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      })

      locationMarkerRef.current = L.marker(
        [locationTarget.lat, locationTarget.lon], { icon: locationIcon }
      ).addTo(mapInstanceRef.current)

      setTimeout(() => {
        if (locationMarkerRef.current) {
          locationMarkerRef.current.remove()
          locationMarkerRef.current = null
        }
      }, 3000)
    } catch (e) {}
  }, [locationTarget])

  return (
    <div ref={mapRef} style={{ height: '100%', width: '100%', zIndex: 1 }} />
  )
}

export default MapView
